import { zodResolver } from "@hookform/resolvers/zod";

import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  type ParticipantPassDto,
  createParticipantPassDtoSchema,
} from "slde-api/dto-schemas";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ParticipantPassFormProps {
  defaultValues?: ParticipantPassDto;
  onSubmit: (values: ParticipantPassDto) => void;

  disabled?: boolean;
  loading?: boolean;
}
export function ParticipantPassForm({
  onSubmit: onSubmitProps,
  defaultValues,
  disabled,
  loading,
}: ParticipantPassFormProps) {
  const form = useForm<ParticipantPassDto>({
    resolver: zodResolver(createParticipantPassDtoSchema),
    defaultValues,
  });

  function onSubmit(values: ParticipantPassDto) {
    onSubmitProps(values);
  }

  return (
    <div className="flex gap-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (e) => console.log(e))}
          className="space-y-4"
        >
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Typ</FormLabel>
                  <FormControl>
                    <Select onValueChange={onChange}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue
                          placeholder={
                            value === "doctor" ? "Lekarz" : "Student"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="doctor">Lekarz</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="identifier"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Numer</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="PWZ / Legitymacja"
                      value={value ?? ""}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <Select onValueChange={onChange}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue
                          placeholder={
                            value === "blocked" ? "Zablokowany" : "Aktywny"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="active">Aktywny</SelectItem>
                          <SelectItem value="blocked">Zablokowany</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="comments"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Komentarz (opcjonalnie)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Zweryfikowany..."
                      value={value ?? ""}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={disabled || loading}>
            {!!defaultValues ? "Edytuj" : "Dodaj"}
            {loading && <Loader2 className="animate-spin" />}
          </Button>
        </form>
      </Form>
    </div>
  );
}
